import React from "react";
import { NeoButton } from "../../../../../components/Common";
import { FieldArray } from "formik";
import { DEFAULT_SET_BY_TYPE } from "../../constants";
import { SetRowByType } from "./SetRowByType";

/**
 * Indica se o método exige agrupamento visual entre exercícios.
 */
const isGroupedMethod = (method) =>
    ["biset", "triset", "giantset", "superset", "circuit"].includes(method);

/**
 * Tabela de séries/exercícios de um bloco de treino.
 * Renderiza cada SetRowByType e o conector visual para métodos agrupados.
 *
 * @param {Object}   block             - Dados do bloco (formik values)
 * @param {number}   blockIndex        - Índice do bloco
 * @param {string}   namePrefix        - Prefixo do campo formik (e.g. "blocks")
 * @param {string}   blockWorkoutType  - Tipo de treino efetivo
 * @param {Object}   method            - Config do método de treino (label, color, desc)
 * @param {Function} handleChange      - Formik handleChange
 * @param {Function} duplicateSet      - Callback p/ duplicar uma série
 * @param {Function} replaceStrengthExercise - Callback p/ trocar exercício de musculação
 * @param {Function} handleSaveSwimSet - Callback p/ salvar set de natação na lib
 * @param {Function} handleSaveStrengthExercise - Callback p/ salvar exercício na lib
 * @param {Array}    runningPaceGroups - Grupos de pace para corrida
 */
const SetsTable = ({
    block,
    blockIndex,
    namePrefix,
    blockWorkoutType,
    method,
    handleChange,
    duplicateSet,
    replaceStrengthExercise,
    handleSaveSwimSet,
    handleSaveStrengthExercise,
    runningPaceGroups = [],
}) => {
    const grouped = isGroupedMethod(block.method);
    const isStrength = blockWorkoutType === "strength";

    return (
        <FieldArray name={`${namePrefix}.${blockIndex}.sets`}>
            {(setArrayHelpers) => (
                <div className="d-flex flex-column mb-2" style={{ gap: grouped ? 0 : '0.5rem' }}>
                    {(block.sets || []).map((set, setIndex) => (
                        <div key={set.id || setIndex} className="position-relative">
                            {/* ── Conector visual entre exercícios agrupados ── */}
                            {isStrength && grouped && setIndex > 0 && (
                                <div
                                    className="d-flex align-items-center justify-content-center"
                                    style={{
                                        height: 20,
                                        position: "relative",
                                    }}
                                >
                                    <div
                                        className="workout-connector-line"
                                        style={{ "--method-color": method.color }}
                                    />
                                    <span
                                        className="font-size-9 px-1 py-0 fw-bold position-relative bg-white workout-connector-badge"
                                        style={{ "--method-color": method.color }}
                                    >
                                        +
                                    </span>
                                </div>
                            )}

                            <SetRowByType
                                workoutType={blockWorkoutType}
                                baseName={`${namePrefix}.${blockIndex}.sets`}
                                setIndex={setIndex}
                                set={set}
                                handleChange={handleChange}
                                onRemove={() => setArrayHelpers.remove(setIndex)}
                                onDuplicate={
                                    blockWorkoutType === "strength" || blockWorkoutType === "running"
                                        ? () => duplicateSet(setArrayHelpers, setIndex, set)
                                        : undefined
                                }
                                onReplaceExercise={
                                    blockWorkoutType === "strength"
                                        ? (exerciseId) => replaceStrengthExercise(blockIndex, setIndex, set, exerciseId)
                                        : undefined
                                }
                                onSaveToLibrary={
                                    blockWorkoutType === "swimming"
                                        ? handleSaveSwimSet
                                        : (blockWorkoutType === "strength" ? handleSaveStrengthExercise : undefined)
                                }
                                runningPaceGroups={runningPaceGroups}
                            />
                        </div>
                    ))}
                    <NeoButton
                        type="button"
                        variant="outline-primary"
                        size="sm"
                        icon="mdi-plus"
                        className="mt-2"
                        onClick={() => setArrayHelpers.push({
                            id: Date.now().toString(),
                            ...DEFAULT_SET_BY_TYPE[blockWorkoutType]
                        })}
                    >
                        {blockWorkoutType === "swimming" ? "Adicionar Set" : (blockWorkoutType === "wod" ? "Adicionar Movimento" : (blockWorkoutType === "running" ? "Adicionar Segmento" : "Adicionar Exercício"))}
                    </NeoButton>
                </div>
            )}
        </FieldArray>
    );
};

export default SetsTable;
